import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationPageItemDto } from '@asset-metrix/portal-gateway-client-js';
import {
  dateFormatter,
  NavigationItem,
  SVGIcon,
} from '@asset-metrix/portal-ui';

import { useDataTimestampsQuery } from '../../api/portalGateway/dataRefreshTime';

import styles from './dataRefreshDateNavigationItem.module.scss';

export type DataRefreshDateNavigationItemProps = {
  collapsed: boolean;
  navigationData: NavigationPageItemDto;
};

export function DataRefreshDateNavigationItem({
  collapsed,
  navigationData,
}: DataRefreshDateNavigationItemProps) {
  const { t } = useTranslation();
  const { data: timestampData } = useDataTimestampsQuery();

  if (!timestampData) {
    return null;
  }

  const { refreshTimestampId, closedQuarterTimestampId } = navigationData;
  const { timestamps } = timestampData;

  const refreshTimestamp = refreshTimestampId
    ? timestamps[refreshTimestampId]
    : undefined;
  const closedQuarterTimestamp = closedQuarterTimestampId
    ? timestamps[closedQuarterTimestampId]
    : undefined;

  // exit if both are not defined
  if (refreshTimestamp == null && closedQuarterTimestamp == null) {
    return null;
  }

  const formattedRefreshDate = dateFormatter(refreshTimestamp, {
    dateFormat: 'fullMonthDayYearWithTime',
  });

  const formattedClosedQuarterDate = dateFormatter(closedQuarterTimestamp, {
    dateFormat: 'fullMonthDayYear',
  });

  const getSublabels = (): (string | ReactNode)[] => {
    if (formattedRefreshDate && formattedClosedQuarterDate) {
      return [
        formattedRefreshDate,
        <>
          <SVGIcon className={styles['check-icon']} name="check" />
          {formattedClosedQuarterDate}
        </>,
      ];
    }

    if (formattedRefreshDate) {
      return [formattedRefreshDate];
    }

    if (formattedClosedQuarterDate) {
      return [formattedClosedQuarterDate];
    }

    return [];
  };

  const getToolTip = () => {
    if (formattedRefreshDate && formattedClosedQuarterDate) {
      return t('app.dataRefresh.tooltip', {
        formattedRefreshDate: formattedRefreshDate,
        formattedClosedQuarterDate: formattedClosedQuarterDate,
        interpolation: {
          escapeValue: false,
        },
      });
    }

    if (formattedRefreshDate) {
      return t('app.dataRefresh.tooltipWithDataRefresh', {
        formattedRefreshDate,
        interpolation: {
          escapeValue: false,
        },
      });
    }

    return t('app.dataRefresh.tooltipWithClosedQuarted', {
      formattedClosedQuarterDate: formattedClosedQuarterDate ?? '',
      interpolation: {
        escapeValue: false,
      },
    });
  };

  return (
    <NavigationItem
      collapsed={collapsed}
      iconName="info"
      label={t(
        `app.dataRefresh.${formattedRefreshDate ? 'dataRefreshlabel' : 'closedQuarterLabel'}`
      )}
      lineClamp={3}
      sublabels={getSublabels()}
      tooltip={getToolTip()}
    />
  );
}
